import React, { useEffect, useState } from "react";
import config from "../config.js";
import { detailToString } from "../utils/apiError";
import { putToS3WithProgress, fmtBytes } from "../utils/taxCertificates";

/**
 * TaxCertificatePortal — PUBLIC customer-facing page at /tax-certificate/<token>.
 *
 * No site auth and no app chrome (same pattern as RevisionApprovalPortal):
 * the 256-bit token in the URL is the only credential and the
 * /portal/tax-certificate/* API routes are API-key-exempt. The customer sees
 * which Kansas form(s) we asked for with the KDOR download links, KanCraft's
 * seller details to copy onto the form, and an upload box for the signed
 * copy. Nothing else about their account is shown.
 */
export default function TaxCertificatePortal() {
  const API_BASE = config.API_BASE_URL;
  const token = window.location.pathname.split("/tax-certificate/")[1] || "";

  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [file, setFile] = useState(null);
  const [formType, setFormType] = useState("");
  const [signerName, setSignerName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/portal/tax-certificate/${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
        if (!cancelled) {
          setData(body);
          if (body.forms?.length === 1) setFormType(body.forms[0].code);
        }
      } catch (e) {
        if (!cancelled) setLoadErr(String(e.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE, token]);

  async function upload() {
    setErr(null);
    if (!file) { setErr("Choose the signed certificate file first."); return; }
    if (data?.max_file_bytes && file.size > data.max_file_bytes) { setErr(`That file is too large (max ${fmtBytes(data.max_file_bytes)}).`); return; }
    setBusy(true);
    try {
      const contentType = file.type || "application/octet-stream";
      const post = async (path, body) => {
        const res = await fetch(`${API_BASE}/portal/tax-certificate/${encodeURIComponent(token)}${path}`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
        const b = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
        return b;
      };
      const u = await post("/upload-url", { file_name: file.name, content_type: contentType, file_size: file.size });
      setProgress(0);
      await putToS3WithProgress(u.upload_url, file, u.content_type, setProgress);
      const r = await post("/confirm", {
        s3_key: u.s3_key, file_name: file.name, content_type: u.content_type,
        form_type: formType || null, signer_name: signerName.trim() || null, tax_id: taxId.trim() || null,
      });
      setResult(r);
      window.scrollTo(0, 0);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  const seller = data?.seller || { name: "KanCraft MFG", address: "583 E 47 Hwy, Girard, Kansas 66743", website: "www.KanCraftMFG.com", email: "info@kancraftmfg.com" };

  const shell = (children) => (
    <div style={{ background: "#f2f4f7", minHeight: "100vh", padding: "24px 12px" }}>
      <div className="mx-auto bg-white shadow-sm rounded" style={{ maxWidth: "760px", padding: "28px 30px" }}>
        {children}
        <div className="text-center text-muted mt-4" style={{ fontSize: "0.75rem" }}>
          {seller.name} · {seller.address} · {seller.website}
        </div>
      </div>
    </div>
  );

  if (loadErr) {
    return shell(
      <div className="text-center py-5">
        <h4>Link not found</h4>
        <p className="text-muted">This upload link is invalid or no longer available. Please contact {seller.name} at {seller.email} for a new one.</p>
      </div>
    );
  }
  if (!data) return shell(<div className="text-center py-5 text-muted">Loading…</div>);

  const header = (
    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 border-bottom pb-3 mb-3">
      <div className="d-flex align-items-center gap-3">
        {seller.logo_url && <img src={seller.logo_url} alt={seller.name} style={{ height: "58px" }} />}
        <div>
          <div className="fw-bold" style={{ fontSize: "1.05rem" }}>{seller.name}</div>
          <div className="text-muted" style={{ fontSize: "0.8rem" }}>{seller.address}</div>
        </div>
      </div>
      <div className="text-end">
        <div className="fw-bold" style={{ fontSize: "1.1rem" }}>SALES TAX<br />EXEMPTION CERTIFICATE</div>
        <div className="text-muted" style={{ fontSize: "0.8rem" }}>for {data.customer_name}</div>
      </div>
    </div>
  );

  const status = result ? "uploaded" : data.status;

  if (status === "expired" || status === "closed") {
    return shell(<>{header}
      <div className="text-center py-4">
        <h5>{status === "expired" ? "This upload link has expired" : "This request is closed"}</h5>
        <p className="text-muted">Please contact {seller.name} at <a href={`mailto:${seller.email}`}>{seller.email}</a> and we'll send a fresh link — or simply reply to our email with the signed certificate attached.</p>
      </div>
    </>);
  }

  if (status === "complete") {
    return shell(<>{header}
      <div className="text-center py-4">
        <h5>✅ Your certificate is on file</h5>
        <p className="text-muted">Thank you — {seller.name} has verified and filed your exemption certificate. Nothing more is needed.</p>
      </div>
    </>);
  }

  const uploaded = result?.uploaded || data.uploaded;

  return shell(<>{header}
    {status === "uploaded" && (
      <div className="alert alert-success">
        <strong>Thank you — we received your certificate.</strong>{" "}
        {uploaded?.file_name ? <>({uploaded.file_name})</> : null} {seller.name} will review it and keep it on file.
        {" "}If you need to replace it, you can upload a corrected copy below.
      </div>
    )}

    <p>
      Hello{data.contact_name ? ` ${data.contact_name}` : ""}, {seller.name} needs a signed Kansas sales tax exemption certificate on file to keep invoicing <strong>{data.customer_name}</strong> without sales tax. Download the form that fits your business, fill it in, sign it, and upload it here.
    </p>

    <h6 className="mt-3">1. Download and complete the form</h6>
    <ul className="list-unstyled">
      {data.forms.map((f) => (
        <li key={f.code} className="mb-2 p-2 border rounded">
          <div><strong>{f.code}</strong> — {f.title}</div>
          <div className="text-muted small">Use this if: {f.who}</div>
          {f.url && <a className="btn btn-sm btn-outline-primary mt-1" href={f.url} target="_blank" rel="noreferrer">Download {f.code} (PDF)</a>}
        </li>
      ))}
    </ul>
    {data.forms.length > 1 && <div className="text-muted small mb-2">Only one form is needed — pick the one that describes your business.</div>}

    <h6 className="mt-3">2. Seller information to put on the form</h6>
    <div className="p-2 bg-light border rounded mb-2">
      <div><strong>{seller.name}</strong></div>
      <div>{seller.address}</div>
    </div>
    {data.note && <div className="alert alert-light border small">{data.note}</div>}

    <h6 className="mt-3">3. Upload the signed copy</h6>
    <div className="row g-2 align-items-end">
      <div className="col-md-7">
        <label className="form-label small mb-1">Signed certificate (PDF or a clear photo)</label>
        <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,application/pdf,image/*"
               onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy} />
        {file && <div className="form-text">{file.name} · {fmtBytes(file.size)}</div>}
      </div>
      <div className="col-md-5">
        <label className="form-label small mb-1">Which form did you sign?</label>
        <select className="form-select" value={formType} onChange={(e) => setFormType(e.target.value)} disabled={busy}>
          <option value="">— choose —</option>
          {(data.requestable_forms || []).map((code) => <option key={code} value={code}>{code}</option>)}
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label small mb-1">Signed by (name)</label>
        <input type="text" className="form-control" value={signerName} onChange={(e) => setSignerName(e.target.value)} disabled={busy} />
      </div>
      <div className="col-md-6">
        <label className="form-label small mb-1">Tax registration number on the form (optional)</label>
        <input type="text" className="form-control" value={taxId} onChange={(e) => setTaxId(e.target.value)} disabled={busy} />
      </div>
    </div>
    {progress !== null && (
      <div className="progress mt-3" style={{ height: 8 }}>
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>
    )}
    {err && <div className="alert alert-danger mt-3 mb-0">{err}</div>}
    <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
      <button className="btn btn-primary" onClick={upload} disabled={busy || !file}>
        {busy ? (progress !== null ? `Uploading ${progress}%…` : "Working…") : (status === "uploaded" ? "Upload a replacement" : "Upload signed certificate")}
      </button>
      <span className="text-muted small">Prefer email? Reply to our message with the signed copy attached instead.</span>
    </div>
    {data.expires_at && <div className="text-muted small mt-3">This link is valid until {new Date(data.expires_at).toLocaleDateString("en-US")}.</div>}
  </>);
}
